/**
 * Worker mobile navigation model.
 *
 * The primary bottom-bar destinations mirror the Android worker app exactly:
 * Today, Shifts, Messages, Time, More. Everything the Android app puts behind
 * "More" — availability, project work, profile — stays behind More here too,
 * so a worker moving between the two clients finds the same things in the same
 * places.
 */

export type WorkerMobileScreen =
  | 'home'
  | 'shifts'
  | 'messages'
  | 'time'
  | 'more'
  | 'availability'
  | 'profile'
  | 'worklog'
  | 'decisions'
  | 'mentorship'
  | 'permissions'
  | 'learning'

export type WorkerMobileTab = Extract<WorkerMobileScreen, 'home' | 'shifts' | 'messages' | 'time' | 'more'>

export const WORKER_MOBILE_TABS: Array<{ id: WorkerMobileTab; label: string; icon: string }> = [
  { id: 'home', label: 'Today', icon: '▶' },
  { id: 'shifts', label: 'Shifts', icon: '▦' },
  { id: 'messages', label: 'Messages', icon: '✉' },
  { id: 'time', label: 'Time', icon: '⏱' },
  { id: 'more', label: 'More', icon: '☰' },
]

/** Screens reached from More keep More highlighted, as the Android nav does. */
export const MORE_OWNED_SCREENS: WorkerMobileScreen[] = [
  'more', 'availability', 'profile', 'worklog', 'decisions', 'mentorship', 'permissions',
]

export function activeTabFor(screen: WorkerMobileScreen): WorkerMobileTab | null {
  if (screen === 'learning') return null
  if (MORE_OWNED_SCREENS.includes(screen)) return 'more'
  return screen as WorkerMobileTab
}

/** `?tab=` deep links from the manifest shortcuts and notification clicks. */
export function screenFromQuery(search: string): WorkerMobileScreen | null {
  const value = new URLSearchParams(search).get('tab')
  if (!value) return null
  const allowed: WorkerMobileScreen[] = [
    'home', 'shifts', 'messages', 'time', 'more', 'availability',
    'profile', 'worklog', 'decisions', 'mentorship', 'permissions', 'learning',
  ]
  return allowed.includes(value as WorkerMobileScreen) ? (value as WorkerMobileScreen) : null
}
